import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorDisplay } from './ErrorDisplay';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorType: 'recoverable' | 'fatal' | 'chunk' | 'unknown';
}

/**
 * Classify error type to show appropriate UI
 */
function classifyError(error: Error | undefined): State['errorType'] {
  if (!error) return 'unknown';
  
  const message = error.message || '';
  const name = error.name || '';
  
  // Chunk loading errors - likely a new deployment
  if (
    message.includes('chunk') || 
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed')
  ) {
    return 'chunk';
  }
  
  // Type errors during render - usually recoverable with retry
  if (name === 'TypeError' && (
    message.includes('undefined') ||
    message.includes('null') ||
    message.includes('Cannot read properties')
  )) {
    return 'recoverable';
  }
  
  // Network errors - recoverable
  if (message.includes('NetworkError') || message.includes('Failed to fetch')) {
    return 'recoverable';
  }
  
  return 'fatal';
}

/**
 * Error Boundary to catch and recover from React errors
 * Particularly important for iOS PWA crash recovery
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorType: 'unknown' };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true, 
      error,
      errorType: classifyError(error)
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
      errorType: classifyError(error)
    });
  }

  handleReset = () => {
    // Show recovery message
    this.setState({ hasError: false, error: undefined, errorInfo: undefined, errorType: 'unknown' });
    
    // Save recovery state
    sessionStorage.setItem('app-recovering', 'true');
    
    // Force a clean reload to recover from crashes
    window.location.href = '/';
  };
  
  handleRetry = () => {
    // Just reset state without full page reload - for recoverable errors
    this.setState({ hasError: false, error: undefined, errorInfo: undefined, errorType: 'unknown' });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Check if we're recovering
      const isRecovering = sessionStorage.getItem('app-recovering') === 'true';
      
      if (isRecovering) {
        // Clear recovery flag after showing message
        setTimeout(() => {
          sessionStorage.removeItem('app-recovering');
        }, 2000);
        
        return (
          <div className="min-h-screen bg-black flex items-center justify-center p-4">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-400 mx-auto"></div>
              <h3 className="text-xl font-bold text-white">Restoring your session...</h3>
              <p className="text-white/70">Welcome back! Setting things up for you.</p>
            </div>
          </div>
        );
      }

      // Different UI based on error type
      const { errorType } = this.state;
      
      if (errorType === 'chunk') {
        return (
          <div className="min-h-screen bg-black flex items-center justify-center p-4">
            <ErrorDisplay
              title="New Version Available"
              description="A new version of BuxBox is ready. Please refresh to continue."
              action={this.handleReset}
              actionLabel="Refresh"
            />
          </div>
        );
      }
      
      if (errorType === 'recoverable') {
        return (
          <div className="min-h-screen bg-black flex items-center justify-center p-4">
            <ErrorDisplay
              title="Something went wrong"
              description="We hit a snag loading this page. Try again or refresh if the problem persists."
              action={this.handleRetry}
              actionLabel="Try Again"
            />
          </div>
        );
      }

      // Fatal/unknown errors
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
          <ErrorDisplay
            title="Oops! Something went wrong"
            description="We're sorry, but something unexpected happened. Please refresh the page."
            action={this.handleReset}
            actionLabel="Refresh"
          />
        </div>
      );
    }

    return this.props.children;
  }
}
